import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignService } from '../../services/campaignService';
import { referenceCodeTypeService } from '../../services/referenceCodeTypeService';
import { templateService }  from '../../services/templateService';
import { contactGroupService } from '../../services/contactGroupService';
import { ChevronLeft, ChevronRight, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { 
  convertLocalToUTC, 
  convertUTCToLocal, 
  formatUTCToLocalDisplay, 
  getCurrentLocalDateTime,
  localDateTimeToDate,
  utcIsoToDate,
  isLocalDateTimeInPast
} from '../../utils/timezoneUtils';

const CampaignForm = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!campaignId;
  
  const [formData, setFormData] = useState({
    campaignName: '',
    campaignType: '',
    campaignStatus: 'SCHEDULED',
    scheduledTime: '',
    endTime: '',
    campaignMessages: [{
      messageTemplateId: '',
      medium: '',
      scheduledTime: '',
      contactGroupId: ''
    }]
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  
  // Template variables and preview state
  const [templateVariables, setTemplateVariables] = useState({}); // { messageIndex: { variableExpression: { type: 'TEXT'|'PARAMS', value: string } } }
  const [showVariables, setShowVariables] = useState({}); // { messageIndex: boolean }
  const [messagePreviews, setMessagePreviews] = useState({}); // { messageIndex: string }
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  
  // Read-only state
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Check if any message has scheduled time in the past
  const hasAnyMessageScheduledInPast = () => {
    return formData.campaignMessages.some(message => {
      return isLocalDateTimeInPast(message.scheduledTime);
    });
  };

  // Update read-only state when form data changes
  useEffect(() => {
    setIsReadOnly(hasAnyMessageScheduledInPast());
  }, [formData.campaignMessages]);

  useEffect(() => {
    if (isEdit) {
      fetchCampaignData();
    }
    fetchTemplates();
    fetchCampaignTypes();
    fetchContactGroups();
  }, [campaignId, isEdit]);

  const fetchCampaignData = async () => {
    try {
      const campaigns = await campaignService.getCampaigns(null); // Get all campaigns regardless of year
      const campaign = campaigns.find(c => c.campaignId === campaignId);
      if (campaign) {
        setFormData({
          campaignName: campaign.campaignName || '',
          campaignType: campaign.campaignType || '',
          campaignStatus: campaign.campaignStatus || 'SCHEDULED',
          scheduledTime: convertUTCToLocal(campaign.scheduledTime),
          endTime: convertUTCToLocal(campaign.endTime),
          campaignMessages: (campaign.campaignMessages || []).map(msg => ({
            ...msg,
            scheduledTime: convertUTCToLocal(msg.scheduledTime)
          }))
        });

        // Load existing variables from campaign messages
        loadVariablesFromCampaignMessages(campaign.campaignMessages || []);
      }
    } catch (err) {
      setError('Failed to fetch campaign data');
      console.error('Error fetching campaign:', err);
    }
  };

  const loadVariablesFromCampaignMessages = (campaignMessages) => {
    try {
      const variablesData = {};
      const previewsData = {};
      const showVariablesData = {};

      for (let i = 0; i < campaignMessages.length; i++) {
        const message = campaignMessages[i];
        
        // Generate preview for all messages with templates
        if (message.messageTemplateId) {
          const template = templates.find(t => t.templateId === message.messageTemplateId);
          if (template) {
            // Use variables directly from the message response if available
            if (message.variables && message.variables.length > 0) {
              // Convert variables to the format expected by the component
              const messageVariables = {};
              message.variables.forEach(variable => {
                messageVariables[variable.variableExpression] = {
                  type: variable.type || 'TEXT',
                  value: variable.value || ''
                };
              });
              
              variablesData[i] = messageVariables;
              showVariablesData[i] = true;
              
              // Generate preview with variables
              const preview = generatePreview(template, messageVariables);
              previewsData[i] = preview;
            } else {
              // Generate preview without variables
              const preview = generatePreview(template, {});
              previewsData[i] = preview;
            }
          }
        }
      }

      setTemplateVariables(variablesData);
      setMessagePreviews(previewsData);
      setShowVariables(showVariablesData);
      
      // Open sidebar if there are any templates selected
      if (Object.keys(previewsData).length > 0) {
        setIsRightSidebarOpen(true);
      }
    } catch (error) {
      console.error('Error loading variables from campaign messages:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await templateService.getApprovedTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const fetchCampaignTypes = async () => {
    try {
      const data = await referenceCodeTypeService.getAllCampaignTypes();
      setCampaignTypes(data);
    } catch (err) {
      console.error('Error fetching campaign types:', err);
    }
  };

  const fetchContactGroups = async () => {
    try {
      const data = await contactGroupService.getContactGroups();
      setContactGroups(data);
    } catch (err) {
      console.error('Error fetching contact groups:', err);
    }
  };

  // Utility functions for template variables
  const extractVariables = (content) => {
    if (!content) return [];
    const matches = content.match(/\{\{(\d+)\}\}/g);
    return matches ? [...new Set(matches)] : [];
  };

  const generatePreview = (template, variables) => {
    if (!template || !template.content) return '';
    
    let preview = template.content;
    if (template.headerText) {
      preview = `${template.headerText}\n\n${preview}`;
    }
    
    // Replace variables with their values
    Object.keys(variables).forEach(expression => {
      const variable = variables[expression];
      if (variable && variable.value) {
        preview = preview.replace(new RegExp(expression.replace(/[{}]/g, '\\$&'), 'g'), variable.value);
      }
    });
    
    return preview;
  };

  const hasVariables = (template) => {
    if (!template) return false;
    // Use pre-extracted variables from backend
    const bodyVariables = template.bodyVariables || [];
    const headerVariables = template.headerVariables || [];
    return bodyVariables.length > 0 || headerVariables.length > 0;
  };

  const getVariableStatus = (messageIndex) => {
    const template = templates.find(t => t.templateId === formData.campaignMessages[messageIndex]?.messageTemplateId);
    if (!template || !hasVariables(template)) return null;
    
    const variables = templateVariables[messageIndex] || {};
    // Use pre-extracted variables from backend
    const bodyVariables = template.bodyVariables || [];
    const headerVariables = template.headerVariables || [];
    const allVariables = [...bodyVariables, ...headerVariables];
    
    const hasAllValues = allVariables.every(expression => 
      variables[expression] && variables[expression].value && variables[expression].value.trim()
    );
    
    return hasAllValues ? 'complete' : 'incomplete';
  };

  // Helper function to add 5 minutes to a datetime-local string
  const addMinutesToDateTime = (dateTimeString, minutes = 5) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    date.setMinutes(date.getMinutes() + minutes);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${mins}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // If campaign start time (scheduledTime) changed, auto-populate empty message scheduled times
      if (name === 'scheduledTime' && value) {
        updated.campaignMessages = prev.campaignMessages.map(msg => ({
          ...msg,
          scheduledTime: msg.scheduledTime || addMinutesToDateTime(value, 5)
        }));
      }
      
      return updated;
    });
  };

  const handleMessageChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      campaignMessages: prev.campaignMessages.map((msg, i) => 
        i === index ? { ...msg, [field]: value } : msg
      )
    }));

    // If template changed, reset variables and preview
    if (field === 'messageTemplateId') {
      // Initialize all variables from template
      if (value) {
        const template = templates.find(t => t.templateId === value);
        if (template) {
          // Extract all variables from template (content + headerText)
          let bodyVariables = template.bodyVariables || [];
          let headerVariables = template.headerVariables || [];
          
          // Fallback: extract from content and headerText if backend variables not available
          if (bodyVariables.length === 0 && template.content) {
            bodyVariables = extractVariables(template.content);
          }
          if (headerVariables.length === 0 && template.headerText) {
            headerVariables = extractVariables(template.headerText);
          }
          
          const allVariables = [...new Set([...bodyVariables, ...headerVariables])];
          
          // Initialize all variables with empty values
          const initialVars = {};
          allVariables.forEach(expression => {
            initialVars[expression] = {
              type: 'TEXT',
              value: ''
            };
          });
          
          setTemplateVariables(prev => ({
            ...prev,
            [index]: initialVars
          }));
          
          const hasVars = allVariables.length > 0;
          setIsRightSidebarOpen(true);
          setShowVariables(prev => ({
            ...prev,
            [index]: hasVars
          }));
          
          // Generate preview immediately
          const preview = generatePreview(template, initialVars);
          setMessagePreviews(prev => ({
            ...prev,
            [index]: preview
          }));
        } else {
          setTemplateVariables(prev => {
            const newVars = { ...prev };
            delete newVars[index];
            return newVars;
          });
          setMessagePreviews(prev => {
            const newPreviews = { ...prev };
            delete newPreviews[index];
            return newPreviews;
          });
          setShowVariables(prev => {
            const newShow = { ...prev };
            delete newShow[index];
            return newShow;
          });
        }
      } else {
        setTemplateVariables(prev => {
          const newVars = { ...prev };
          delete newVars[index];
          return newVars;
        });
        setMessagePreviews(prev => {
          const newPreviews = { ...prev };
          delete newPreviews[index];
          return newPreviews;
        });
        setShowVariables(prev => {
          const newShow = { ...prev };
          delete newShow[index];
          return newShow;
        });
      }
    }
  };

  const handleVariableChange = (messageIndex, expression, type, value) => {
    setTemplateVariables(prev => ({
      ...prev,
      [messageIndex]: {
        ...prev[messageIndex],
        [expression]: { type, value }
      }
    }));

    // Update preview
    const template = templates.find(t => t.templateId === formData.campaignMessages[messageIndex]?.messageTemplateId);
    if (template) {
      const updatedVariables = {
        ...templateVariables[messageIndex],
        [expression]: { type, value }
      };
      const preview = generatePreview(template, updatedVariables);
      setMessagePreviews(prev => ({
        ...prev,
        [messageIndex]: preview
      }));
    }
  };


  const toggleShowVariables = (messageIndex) => {
    setShowVariables(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
    
    // Open sidebar when showing variables
    if (!showVariables[messageIndex]) {
      setIsRightSidebarOpen(true);
    }
  };

  // Generate previews for all selected templates
  const generateAllPreviews = () => {
    const newPreviews = {};
    formData.campaignMessages.forEach((message, index) => {
      if (message.messageTemplateId) {
        const template = templates.find(t => t.templateId === message.messageTemplateId);
        if (template) {
          const variables = templateVariables[index] || {};
          newPreviews[index] = generatePreview(template, variables);
        }
      }
    });
    setMessagePreviews(newPreviews);
  };

  // Handle manual sidebar opening
  const handleOpenSidebar = () => {
    setIsRightSidebarOpen(true);
    generateAllPreviews();
  };

  const saveVariables = (messageIndex) => {
    // This would typically save to backend
    // For now, we'll just show a success message
    console.log('Saving variables for message', messageIndex, templateVariables[messageIndex]);
  };

  const saveNewMessage = async (messageIndex) => {
    const message = formData.campaignMessages[messageIndex];
    
    if (!message.messageTemplateId || !message.medium || !message.contactGroupId || !message.scheduledTime) {
      setError('Please fill in all required fields for the message');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare message data with variables
      const messageVariables = templateVariables[messageIndex] ? 
        Object.entries(templateVariables[messageIndex]).map(([expression, data]) => ({
          variableExpression: expression,
          value: data.value,
          type: data.type
        })) : [];

      const messageData = {
        messageTemplateId: message.messageTemplateId,
        medium: message.medium,
        contactGroupId: message.contactGroupId,
        scheduledTime: convertLocalToUTC(message.scheduledTime),
        variables: messageVariables
      };

      // Save the message using the new endpoint
      const savedMessage = await campaignService.createMessage(campaignId, messageData);
      
      // Update the form data to replace the temporary message with the saved one
      setFormData(prev => ({
        ...prev,
        campaignMessages: prev.campaignMessages.map((msg, idx) => 
          idx === messageIndex ? { ...savedMessage, isNew: false } : msg
        )
      }));

      // Clear variables for this message since it's now saved
      setTemplateVariables(prev => {
        const newVars = { ...prev };
        delete newVars[messageIndex];
        return newVars;
      });

      // Remove from showVariables since it's saved
      setShowVariables(prev => {
        const newShow = { ...prev };
        delete newShow[messageIndex];
        return newShow;
      });

    } catch (err) {
      setError(err.message || 'Failed to save message');
      console.error('Error saving message:', err);
    } finally {
      setLoading(false);
    }
  };

  const addMessage = () => {
    if (isEdit) {
      // For editing campaigns, we'll add a temporary message to the form
      // The user will need to fill it out and save it individually
      setFormData(prev => ({
        ...prev,
        campaignMessages: [...prev.campaignMessages, {
          messageTemplateId: '',
          medium: '',
          scheduledTime: prev.scheduledTime ? addMinutesToDateTime(prev.scheduledTime, 5) : '',
          contactGroupId: '',
          isNew: true // Flag to indicate this is a new message
        }]
      }));
    } else {
      // For new campaigns, add message to form state
      setFormData(prev => ({
        ...prev,
        campaignMessages: [...prev.campaignMessages, {
          messageTemplateId: '',
          medium: '',
          scheduledTime: prev.scheduledTime ? addMinutesToDateTime(prev.scheduledTime, 5) : '',
          contactGroupId: ''
        }]
      }));
    }
  };

  const removeMessage = (index) => {
    if (formData.campaignMessages.length > 1) {
      setFormData(prev => ({
        ...prev,
        campaignMessages: prev.campaignMessages.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    if (!formData.campaignName.trim()) {
      setError('Campaign name is required');
      return false;
    }
    
    if (!formData.campaignType.trim()) {
      setError('Campaign type is required');
      return false;
    }
    
    if (!formData.scheduledTime) {
      setError('Scheduled time is required');
      return false;
    }
    
    if (formData.endTime && new Date(formData.endTime) <= new Date(formData.scheduledTime)) {
      setError('End time must be after scheduled time');
      return false;
    }
    
    // Only validate campaign messages for new campaigns
    if (!isEdit) {
      const validMessages = formData.campaignMessages.filter(msg => 
        msg.medium.trim() && msg.scheduledTime
      );
      
      if (validMessages.length === 0) {
        setError('At least one valid message is required');
        return false;
      }

      // Validate template variables
      for (let i = 0; i < formData.campaignMessages.length; i++) {
        const message = formData.campaignMessages[i];
        if (message.messageTemplateId) {
          const template = templates.find(t => t.templateId === message.messageTemplateId);
          if (template && hasVariables(template)) {
            const status = getVariableStatus(i);
            if (status === 'incomplete') {
              setError(`Please provide values for all variables in Message ${i + 1}`);
              return false;
            }
          }
        }
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Check for unsaved new messages in edit mode
    if (isEdit) {
      const unsavedMessages = formData.campaignMessages.filter(msg => msg.isNew);
      if (unsavedMessages.length > 0) {
        setError('Please save all new messages before updating the campaign');
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let campaignData;
      
      if (isEdit) {
        // For editing, exclude campaign messages and convert datetime fields
        const { campaignMessages, ...campaignInfo } = formData;
        campaignData = {
          ...campaignInfo,
          scheduledTime: convertLocalToUTC(campaignInfo.scheduledTime),
          endTime: campaignInfo.endTime ? convertLocalToUTC(campaignInfo.endTime) : null
        };
        await campaignService.updateCampaign(campaignId, campaignData);
      } else {
        // For creating, include filtered campaign messages with variables
        const campaignMessagesWithVariables = formData.campaignMessages
          .filter(msg => msg.medium.trim() && msg.scheduledTime)
          .map((msg, index) => {
            const messageVariables = templateVariables[index] ? 
              Object.entries(templateVariables[index]).map(([expression, data]) => ({
                variableExpression: expression,
                value: data.value,
                type: data.type
              })) : [];
            
            return {
              ...msg,
              scheduledTime: convertLocalToUTC(msg.scheduledTime),
              variables: messageVariables
            };
          });
        
        campaignData = {
          ...formData,
          scheduledTime: convertLocalToUTC(formData.scheduledTime),
          endTime: formData.endTime ? convertLocalToUTC(formData.endTime) : null,
          campaignMessages: campaignMessagesWithVariables
        };
        await campaignService.createCampaign(campaignData);
      }
      
      navigate('/campaigns');
    } catch (err) {
      console.error('Error saving campaign:', err);
      
      // Handle specific error types
      if (err.response?.status === 403 && err.response?.data?.message?.includes('License')) {
        setError('License Not Active - Please contact administrator to activate your license');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(err.message || 'Failed to save campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const canEditTimes = () => {
    if (!isEdit) return true;
    
    return !isLocalDateTimeInPast(formData.scheduledTime);
  };

  // Check if the entire form can be edited
  const canEdit = () => {
    // Can't edit if any message has scheduled time in the past
    if (isReadOnly) return false;
    
    // Can't edit campaign times if campaign has started
    return canEditTimes();
  };

  // Get current date/time in the format required by datetime-local input
  const getCurrentDateTime = () => {
    return getCurrentLocalDateTime();
  };

  // Get maximum allowed time for message scheduling (campaign end time or current time + 1 year)
  const getMaxMessageScheduledTime = () => {
    if (formData.endTime) {
      // If campaign has an end time, convert it to local datetime string
      return convertUTCToLocal(formData.endTime);
    }
    
    // If no end time set, allow scheduling up to 1 year from now
    const maxTime = new Date();
    maxTime.setFullYear(maxTime.getFullYear() + 1);
    const year = maxTime.getFullYear();
    const month = String(maxTime.getMonth() + 1).padStart(2, '0');
    const day = String(maxTime.getDate()).padStart(2, '0');
    const hours = String(maxTime.getHours()).padStart(2, '0');
    const minutes = String(maxTime.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className={`grid grid-cols-1 gap-8 transition-all duration-300 ${(isRightSidebarOpen && formData.campaignMessages.some(msg => msg.messageTemplateId)) ? 'lg:grid-cols-3' : 'lg:grid-cols-1'}`}>
        {/* Main Form */}
        <div className={(isRightSidebarOpen && formData.campaignMessages.some(msg => msg.messageTemplateId)) ? 'lg:col-span-2' : 'lg:col-span-1'}>
          <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Campaign' : 'Create New Campaign'}
            </h1>
            <div className="flex items-center space-x-3">
              <button 
                type="button" 
                onClick={() => navigate('/campaigns')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              {/* Toggle Button - only show when sidebar is closed and template is selected */}
              {!isRightSidebarOpen && formData.campaignMessages.some(msg => msg.messageTemplateId) && (
                <button
                  type="button"
                  onClick={handleOpenSidebar}
                  className="p-2 text-gray-400 hover:text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  title="Show Template Variables & Preview"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {!canEdit() && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      {isReadOnly 
                        ? "Cannot edit campaign with messages scheduled in the past."
                        : "Cannot edit campaign times after campaign has started."
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-6">Campaign Information</h2>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="campaignName" className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="campaignName"
                    name="campaignName"
                    value={formData.campaignName}
                    onChange={handleInputChange}
                    required
                    disabled={!canEdit()}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>

                <div>
                  <label htmlFor="campaignType" className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="campaignType"
                    name="campaignType"
                    value={formData.campaignType}
                    onChange={handleInputChange}
                    required
                    disabled={!canEdit()}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Type</option>
                    {campaignTypes.map(type => (
                      <option key={type.codeValue} value={type.codeValue}>
                        {type.codeValue}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="scheduledTime" className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    id="scheduledTime"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={handleInputChange}
                    required
                    min={getCurrentDateTime()}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={!canEdit()}
                  />
                  {!canEditTimes() && (
                    <p className="mt-1 text-sm text-gray-500">
                      Start time cannot be changed after campaign has started
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    id="endTime"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    disabled={!canEdit()}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    min={formData.scheduledTime || getCurrentDateTime()}
                  />
                  {!canEditTimes() && (
                    <p className="mt-1 text-sm text-gray-500">
                      Only end time can be updated after campaign starts
                    </p>
                  )}
                </div>
              </div>
            </div>

            {!isEdit && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-medium text-gray-900">Campaign Messages *</h2>
                  <button 
                    type="button" 
                    onClick={addMessage}
                    disabled={!canEdit()}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    + Add Message
                  </button>
                </div>

              <div className="space-y-6">
                {formData.campaignMessages.map((message, index) => (
                  <div key={index} className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-md font-medium text-gray-900">Message {index + 1}</h3>
                        {message.isNew && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            New
                          </span>
                        )}
                      </div>
                      {formData.campaignMessages.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removeMessage(index)}
                          disabled={!canEdit()}
                          className="text-sm text-red-600 hover:text-red-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor={`medium-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                          Medium <span className="text-red-500">*</span>
                        </label>
                        <select
                          id={`medium-${index}`}
                          value={message.medium}
                          onChange={(e) => handleMessageChange(index, 'medium', e.target.value)}
                          required
                          disabled={!canEdit()}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Select Medium</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="insta">Instagram</option>
                          <option value="fb">Facebook</option>
                          <option value="email">Email</option>
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label htmlFor={`template-${index}`} className="block text-sm font-medium text-gray-700">
                            Message Template
                          </label>
                          {(() => {
                            const template = templates.find(t => t.templateId === message.messageTemplateId);
                            const status = getVariableStatus(index);
                            if (template && hasVariables(template)) {
                              return (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500">
                                    Chosen template has dynamic input provide value here
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => toggleShowVariables(index)}
                                    className={`text-xs font-medium underline ${
                                      status === 'complete' ? 'text-green-600' : 'text-red-600'
                                    }`}
                                  >
                                    Show variables
                                  </button>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <select
                          id={`template-${index}`}
                          value={message.messageTemplateId}
                          onChange={(e) => handleMessageChange(index, 'messageTemplateId', e.target.value)}
                          disabled={!canEdit()}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">No Template</option>
                          {templates.map(template => (
                            <option key={template.templateId} value={template.templateId}>
                              {template.templateName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor={`contactGroup-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                          Target Group
                        </label>
                        <select
                          id={`contactGroup-${index}`}
                          value={message.contactGroupId}
                          onChange={(e) => handleMessageChange(index, 'contactGroupId', e.target.value)}
                          disabled={!canEdit()}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Select Contact Group</option>
                          {contactGroups.map(group => (
                            <option key={group.groupId} value={group.groupId}>
                              {group.groupName} ({group.totalContacts} contacts)
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-sm text-gray-500">
                          Select a contact group to target with this message
                        </p>
                      </div>

                      <div>
                        <label htmlFor={`scheduledTime-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                          Scheduled Time <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="datetime-local"
                          id={`scheduledTime-${index}`}
                          value={message.scheduledTime}
                          onChange={(e) => handleMessageChange(index, 'scheduledTime', e.target.value)}
                          required
                          disabled={!canEdit()}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          min={formData.scheduledTime || getCurrentDateTime()}
                          max={getMaxMessageScheduledTime()}
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Message must be scheduled between campaign start time and {formData.endTime ? 'campaign end time' : '1 year from now'}
                        </p>
                      </div>
                    </div>

                    {/* Save Message Button for new messages in edit mode */}
                    {isEdit && message.isNew && (
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() => saveNewMessage(index)}
                          disabled={loading}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Saving...' : 'Save Message'}
                        </button>
                      </div>
                    )}

                  </div>
                ))}
              </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button 
                type="button" 
                onClick={() => navigate('/campaigns')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading || !canEdit()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Campaign' : 'Create Campaign')}
              </button>
            </div>
          </form>
        </div>
        </div>
        </div>

        {/* Right Sidebar - Template Variables */}
        {isRightSidebarOpen && formData.campaignMessages.some(msg => msg.messageTemplateId) && (
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Sidebar Header with Close Button */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Template Variables</h2>
                    <button
                      type="button"
                      onClick={() => setIsRightSidebarOpen(false)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 space-y-6">
                  {/* Default Message when no variables */}
                  {!Object.keys(showVariables).some(index => showVariables[index]) && (() => {
                    // Check if any template is selected
                    const hasSelectedTemplate = formData.campaignMessages.some(msg => msg.messageTemplateId);
                    const selectedTemplate = hasSelectedTemplate ? 
                      templates.find(t => formData.campaignMessages.some(msg => msg.messageTemplateId === t.templateId)) : null;
                    
                    if (hasSelectedTemplate && selectedTemplate && !hasVariables(selectedTemplate)) {
                      // Template selected but has no variables
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                          <div className="text-blue-400 mb-3">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-blue-900 mb-2">Template has no dynamic variables</h3>
                          <p className="text-sm text-blue-700 mb-4">
                            The selected template "{selectedTemplate.templateName}" does not contain any dynamic variables.
                          </p>
                          <div className="text-xs text-blue-600">
                            <p>• This template will be sent as-is without any variable substitution</p>
                            <p>• No additional configuration is needed</p>
                          </div>
                        </div>
                      );
                    } else {
                      // No template selected
                      return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                          <div className="text-gray-400 mb-3">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Template Variables</h3>
                          <p className="text-sm text-gray-600 mb-4">
                            Select a message template with variables to see them here and configure their values.
                          </p>
                          <div className="text-xs text-gray-500">
                            <p className="mb-1">• Templates with variables like <code className="bg-gray-200 px-1 rounded">{'{{customer_name}}'}</code> will appear here</p>
                            <p>• You can set custom values or use contact data</p>
                          </div>
                        </div>
                      );
                    }
                  })()}

                  {/* Template Variables Panel */}
                  {Object.keys(showVariables).some(index => showVariables[index]) && (
                    <div className="bg-blue-50 rounded-lg border border-blue-200">
                      <div className="p-4 border-b border-blue-200">
                        <h3 className="text-md font-semibold text-blue-900 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-2 text-blue-600" />
                          Active Variables
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                  {Object.keys(showVariables).map(messageIndex => {
                    if (!showVariables[messageIndex]) return null;
                    
                    const index = parseInt(messageIndex);
                    const message = formData.campaignMessages[index];
                    const template = templates.find(t => t.templateId === message?.messageTemplateId);
                    
                    if (!template || !hasVariables(template)) return null;
                    
                    // Use pre-extracted variables from backend
                    let bodyVariables = template.bodyVariables || [];
                    let headerVariables = template.headerVariables || [];
                    
                    // Fallback: extract variables from content and headerText if backend variables are not available
                    if (bodyVariables.length === 0 && template.content) {
                      bodyVariables = extractVariables(template.content);
                    }
                    if (headerVariables.length === 0 && template.headerText) {
                      headerVariables = extractVariables(template.headerText);
                    }
                    
                    const allVariables = [...new Set([...bodyVariables, ...headerVariables])];
                    
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Message {index + 1}</h4>
                          <button
                            type="button"
                            onClick={() => toggleShowVariables(index)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          {allVariables.map(expression => {
                            const variable = templateVariables[index]?.[expression] || { type: 'TEXT', value: '' };
                            return (
                              <div key={expression} className="bg-gray-50 p-3 rounded border">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-gray-700">{expression}</span>
                                  <div className="flex items-center space-x-2">
                                    <label className="flex items-center">
                                      <input
                                        type="radio"
                                        name={`${index}-${expression}-type`}
                                        value="TEXT"
                                        checked={variable.type === 'TEXT'}
                                        onChange={(e) => handleVariableChange(index, expression, e.target.value, variable.value)}
                                        disabled={!canEdit()}
                                        className="mr-1 disabled:cursor-not-allowed"
                                      />
                                      <span className="text-xs text-gray-600">TEXT</span>
                                    </label>
                                    <label className="flex items-center">
                                      <input
                                        type="radio"
                                        name={`${index}-${expression}-type`}
                                        value="PARAMS"
                                        checked={variable.type === 'PARAMS'}
                                        onChange={(e) => handleVariableChange(index, expression, e.target.value, 'contact_name')}
                                        disabled={!canEdit()}
                                        className="mr-1 disabled:cursor-not-allowed"
                                      />
                                      <span className="text-xs text-gray-600">PARAMS</span>
                                    </label>
                                  </div>
                                </div>
                                
                                {variable.type === 'TEXT' ? (
                                  <input
                                    type="text"
                                    value={variable.value}
                                    onChange={(e) => handleVariableChange(index, expression, 'TEXT', e.target.value)}
                                    placeholder={`Enter value for ${expression}`}
                                    disabled={!canEdit()}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                  />
                                ) : (
                                  <select
                                    value={variable.value}
                                    onChange={(e) => handleVariableChange(index, expression, 'PARAMS', e.target.value)}
                                    disabled={!canEdit()}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                  >
                                    <option value="contact_name">contact_name</option>
                                  </select>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        <div className="mt-3 flex justify-between items-center">
                          <button
                            type="button"
                            onClick={() => saveVariables(index)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Save
                          </button>
                          <span className={`text-xs px-2 py-1 rounded ${
                            getVariableStatus(index) === 'complete' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {getVariableStatus(index) === 'complete' ? 'Complete' : 'Incomplete'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
              </div>
            )}

                  {/* Message Preview Panel */}
                  {formData.campaignMessages.some(msg => msg.messageTemplateId) && (
                    <div className="bg-green-50 rounded-lg border border-green-200">
                      <div className="p-4 border-b border-green-200">
                        <h3 className="text-md font-semibold text-green-900 flex items-center">
                          <ChevronRight className="h-4 w-4 mr-2 text-green-600" />
                          Message Preview
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {formData.campaignMessages.map((message, index) => {
                          if (!message.messageTemplateId) return null;
                          
                          const template = templates.find(t => t.templateId === message.messageTemplateId);
                          if (!template) return null;
                          
                          // Get preview for this message
                          const preview = messagePreviews[index] || generatePreview(template, templateVariables[index] || {});
                          
                          return (
                            <div key={index} className="border border-green-200 rounded-lg p-3 bg-white">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Message {index + 1}</h4>
                              <div className="bg-gray-50 p-3 rounded border">
                                <p className="text-xs text-gray-800 whitespace-pre-wrap">{preview}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Help Panel */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Template Variables Help
                    </h4>
                    <ul className="text-xs text-gray-700 space-y-1">
                      <li>• <strong>TEXT:</strong> Enter custom values for variables</li>
                      <li>• <strong>PARAMS:</strong> Use dynamic contact data</li>
                      <li>• Variables must be filled before creating campaign</li>
                      <li>• Preview updates in real-time as you type</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default CampaignForm;
